import React, { useEffect, useRef } from 'react';
import { Badge, Spinner } from 'elmo-elements';
import { useGetImportStatusQuery } from '@/modules/services';

// Backend progress granularity is ~20-30s per slide, so 5s polling is already
// generous (LMS2-5049 relaxed this from the previous 3s full-list refetch).
export const IMPORT_STATUS_POLL_MS = 5000;

export interface ImportStatusBadgeProps {
  elearningId: string;
  initialStatus: 'queued' | 'importing';
  /**
   * Fired once, when the import transitions to ready/failed. The parent list
   * re-fetches itself on this signal instead of polling the whole page.
   */
  onSettled: () => void;
}

/**
 * Status badge for a My Library row whose elearning is mid-import. Polls the
 * lightweight import-status endpoint for just this elearning, so the list no
 * longer re-fetches the entire paginated page on an interval during imports.
 */
const ImportStatusBadge = ({
  elearningId,
  initialStatus,
  onSettled,
}: ImportStatusBadgeProps): React.ReactElement | null => {
  const { data } = useGetImportStatusQuery(elearningId, {
    pollingInterval: IMPORT_STATUS_POLL_MS,
  });
  // Before the first poll lands, show the status the list row was fetched with.
  // A polled null means the import markers were cleared server-side (e.g. the
  // module was edited) — the import no longer exists, so treat it as settled
  // rather than mapping it back to the initial spinner state.
  const status = data ? data.importStatus : initialStatus;

  const settledRef = useRef(false);
  useEffect(() => {
    if (
      !settledRef.current &&
      (status === 'ready' || status === 'failed' || status === null)
    ) {
      settledRef.current = true;
      onSettled();
    }
  }, [status, onSettled]);

  if (status === 'failed') {
    return <Badge type="danger">Import failed</Badge>;
  }

  if (status === 'ready' || status === null) {
    // onSettled() has asked the parent to re-fetch the list; the refreshed row
    // renders the real badge, so show nothing rather than a stale spinner.
    return null;
  }

  return (
    <span className="import-status import-status--importing">
      <Spinner size="xs" />
      <span>{status === 'queued' ? 'Queued' : 'Importing'}&hellip;</span>
    </span>
  );
};

export default ImportStatusBadge;
